import BaseModel from '@core/model';

import SubscriptionTypeEnum from '@constants/enums/subscription/type';

import SubscriptionApi from '@packages/subscriptions/api';
import { normaliseDateRange } from '@packages/rate-plans/utils/currentSubscription';
import { isSubscriptionCancellable } from '@packages/subscriptions/utils/subscriptionCancel';

import clock from '@plugins/dayjs';

import Adviser from '@/models/Adviser';
import BillingGroup from '@/models/BillingGroup';
import RatePlan from '@/models/RatePlan';

export class Subscription extends BaseModel {
    static name = 'Subscription';
    static entity = 'subscriptions';
    static Api = SubscriptionApi;

    static fields() {
        return {
            ...super.fields(),
            id: this.attr(null),
            gi_ref: this.string(null).nullable(),
            billing_group_id: this.string(null).nullable(),
            billing_group: this.belongsTo(BillingGroup, 'billing_group_id'),
            rate_plan_id: this.string(null).nullable(),
            rate_plan: this.belongsTo(RatePlan, 'rate_plan_id'),
            subscription_type: this.enum(SubscriptionTypeEnum).nullable(),
            date_range: this.attr(null).nullable(),
            investor_id: this.string(null).nullable(),
            adviser_id: this.string(null).nullable(),
            adviser: this.belongsTo(Adviser, 'adviser_id'),
            investors_count: this.number(null).nullable()
        };
    }

    get normalised_date_range() {
        return normaliseDateRange(this.date_range);
    }

    get start_date() {
        return this.normalised_date_range?.start || null;
    }

    get end_date() {
        return this.normalised_date_range?.end || null;
    }

    get is_scheduled() {
        if (!this.start_date) {
            return false;
        }

        const today = clock().tz('Europe/London').format('YYYY-MM-DD');

        return this.start_date > today;
    }

    get is_cancellable() {
        return isSubscriptionCancellable(this);
    }

    static mock(overrides = {}) {
        return {
            id: faker => faker.string.uuid(),
            gi_ref: faker => overrides.gi_ref || `GIBS${faker.string.numeric(4)}`,
            billing_group_id: faker => overrides.billing_group_id || faker.string.uuid(),
            rate_plan_id: faker => overrides.rate_plan_id || faker.string.uuid(),
            subscription_type: faker =>
                overrides.subscription_type || faker.helpers.arrayElement(Object.values(SubscriptionTypeEnum)),
            date_range: () => ({
                start: '2024-01-01',
                end: null
            }),
            investor_id: faker => overrides.investor_id || faker.string.uuid(),
            adviser_id: faker => overrides.adviser_id || faker.string.uuid(),
            investors_count: faker => overrides.investors_count ?? faker.number.int({ min: 1, max: 3 })
        };
    }
}

export default Subscription;
